import { computed, Directive, effect, ElementRef, forwardRef, HostListener, inject, input, model, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// make Auto Numeric JS recognized in Typescript
import AutoNumeric from 'autonumeric';

import { BaasAutoNumericOptions } from './baas-auto-numeric-options.type';

@Directive({
    selector: 'input[baasAutoNumeric]',
    exportAs: 'baasAutoNumeric',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BaasAutoNumericInput),
            multi: true
        }
    ],
    standalone: true
})
/** BAR wrapper around AutoNumericJS */
export class BaasAutoNumericInput implements ControlValueAccessor, OnInit, OnDestroy {
    readonly #input = inject<ElementRef<HTMLInputElement>>(ElementRef);

    readonly baasAutoNumeric = input<BaasAutoNumericOptions>(undefined);
    readonly disabled = model<boolean>(undefined);

    readonly #ignoreInvalidPaste = computed(
        () => this.baasAutoNumeric() != null && this.baasAutoNumeric().onInvalidPaste && this.baasAutoNumeric().onInvalidPaste.toLowerCase() === 'ignore'
    );

    #instance: any;
    #fnOnChange: any;
    #fnOnTouched: any;

    constructor() {
        effect(() => this.#instance.update(this.baasAutoNumeric()));
        effect(() => (this.#input.nativeElement.disabled = this.disabled()));
    }

    /**
     * Handles the changes on input.
     * @param value The value in the input
     */
    @HostListener('input', ['$event'])
    handleInput(event: Event) {
        this.#handleChange((event.target as HTMLInputElement).value);
    }

    /**
     * Handles the changes on blur.
     * @param value The value in the input
     */
    @HostListener('blur', ['$event'])
    handleChangeBlur(event: Event) {
        this.#fnOnTouched();
    }

    @HostListener('paste', ['$event'])
    handlePaste(e): void {
        if (this.#ignoreInvalidPaste() && this.#instance._onPasteFunc != null) {
            try {
                // call the original paste func from AutoNumeric
                this.#instance._onPasteFunc(e);
            } catch {
                // NOTE we don't want hard errors when the paste fails, just let it fails with no action
            }
        }
    }

    ngOnInit(): void {
        Object.defineProperty(this.#input.nativeElement, 'numericValue', {
            get: () => this.#instance.getNumber(),
            set: (newVal) => {
                this.writeValue(newVal);
            }
        });

        // instantiate the AutoNumeric JS
        this.#instance = new AutoNumeric(this.#input.nativeElement, this.baasAutoNumeric());

        const origPreparePastedText = this.#instance._preparePastedText;
        this.#instance._preparePastedText = (text) => {
            // in case the consumer allows dot als alternative but a comma is expected we assume the dot is a comma
            if (
                this.baasAutoNumeric() &&
                this.baasAutoNumeric().decimalCharacter === ',' &&
                this.baasAutoNumeric().decimalCharacterAlternative === '.' &&
                this.baasAutoNumeric().digitGroupSeparator !== '.' &&
                text.indexOf('.') > -1
            ) {
                text = text.replace('.', ',');
            }
            return origPreparePastedText.call(this.#instance, text);
        };

        this.#input.nativeElement.setAttribute('autocomplete', 'off');
        this.#input.nativeElement.className += ` text-${this.baasAutoNumeric().align ? this.baasAutoNumeric().align : 'end'}`;
        // unbind the normal paste event (because it causes hard errors) in case we don't want those errors (option in AutoNumeric)
        if (this.#ignoreInvalidPaste() && this.#instance._onPasteFunc != null) {
            this.#input.nativeElement.removeEventListener('paste', this.#instance._onPasteFunc);
        }
    }

    ngOnDestroy(): void {
        if (this.#instance != null) {
            // remove the AutoNumeric instance on destroy
            this.#instance.remove();
        }
    }

    /**
     * Happens when value is passed from the form system.
     * @param value The value from the form system which is always a number or null.
     */
    writeValue(value: number): void {
        if (value == null) {
            // clear the value
            this.#instance.clear();
        } else {
            // assign the value to the AutoNumeric JS (set)
            this.#instance.set(value);
        }
    }

    registerOnChange(fn: any): void {
        // register the on change callback
        this.#fnOnChange = fn;
    }
    registerOnTouched(fn: any): void {
        // register the on touched callback
        this.#fnOnTouched = fn;
    }

    #handleChange(value: string): void {
        // if empty pass on null
        if (this.#checkValueIsEmpty(value)) {
            this.#instance.clear();
            this.#fnOnChange(null);
        } else {
            // transform to real number
            const number: number = this.#instance.getNumber();
            // pass this number to the form system
            this.#fnOnChange(number);
        }
    }

    #checkValueIsEmpty(value: string) {
        // the value is considered empty if empty or if it only contains the currency symbol (in case it is set)
        return value == '' || (this.baasAutoNumeric()?.currencySymbol && value === this.baasAutoNumeric().currencySymbol);
    }
}
